import React, { useState, useEffect } from 'react';

import { categoryService } from '../../../services/categoryService';
import Icon from '../../../components/AppIcon';

const AddBudgetModal = ({ userId, onClose, onAdd }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  const [formData, setFormData] = useState({
    categoryId: '',
    subcategoryId: '',
    limitAmount: '',
    alertThreshold1: 80,
    alertThreshold2: 95,
    rolloverEnabled: false,
    isRecurring: false,
    recurrenceFrequency: 'monthly',
    endDate: ''
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await categoryService?.getHierarchical();
      // Filter only expense categories
      const expenseCategories = data?.filter(cat => cat?.type === 'expense');
      setCategories(expenseCategories);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({ ...prev, categoryId, subcategoryId: '' }));
    
    // Find the selected parent category and set available subcategories
    const parent = categories?.find(cat => cat?.id === categoryId);
    setSelectedParentCategory(parent);
    setAvailableSubcategories(parent?.subcategories || []);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Determine which category ID to use (subcategory takes precedence if selected)
    const finalCategoryId = formData?.subcategoryId || formData?.categoryId;

    if (!finalCategoryId) {
      setError('Please select a category');
      return;
    }

    if (!formData?.limitAmount || parseFloat(formData?.limitAmount) <= 0) {
      setError('Please enter a valid limit amount');
      return;
    }

    try {
      await onAdd({
        categoryId: finalCategoryId,
        limitAmount: parseFloat(formData?.limitAmount),
        alertThreshold1: formData?.alertThreshold1,
        alertThreshold2: formData?.alertThreshold2,
        rolloverEnabled: formData?.rolloverEnabled,
        isRecurring: formData?.isRecurring,
        recurrenceFrequency: formData?.isRecurring ? formData?.recurrenceFrequency : null,
        endDate: formData?.isRecurring && formData?.endDate ? formData?.endDate : null
      });
    } catch (err) {
      setError(err?.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-24 border-b border-border">
          <h2 className="text-xl font-semibold text-card-foreground">Add Budget Limit</h2>
          <button
            onClick={onClose}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-24">
          {error && (
            <div className="mb-16 p-12 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Category Select */}
          <div className="mb-20">
            <label className="block text-sm font-medium text-foreground mb-8">
              Category *
            </label>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading categories...</div>
            ) : (
              <select
                value={formData?.categoryId}
                onChange={(e) => handleCategoryChange(e?.target?.value)}
                className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
                required
              >
                <option value="">
                  Select a category
                </option>
                {categories?.map((category) => (
                  <option key={category?.id} value={category?.id}>
                    {category?.emoji ? `${category?.emoji} ` : ''}{category?.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Subcategory dropdown - only show if parent category is selected and has subcategories */}
          {formData?.categoryId && availableSubcategories?.length > 0 && (
            <div className="mb-20">
              <label className="block text-sm font-medium text-foreground mb-8">
                Subcategory (Optional)
              </label>
              <select
                value={formData?.subcategoryId}
                onChange={(e) => setFormData({ ...formData, subcategoryId: e?.target?.value })}
                className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
              >
                <option value="">
                  Select a subcategory (optional)
                </option>
                {availableSubcategories?.map((subcategory) => (
                  <option key={subcategory?.id} value={subcategory?.id}>
                    {subcategory?.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Limit Amount */}
          <div className="mb-20">
            <label className="block text-sm font-medium text-foreground mb-8">
              Monthly Limit Amount *
            </label>
            <div className="relative">
              <span className="absolute left-16 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData?.limitAmount}
                onChange={(e) => setFormData({ ...formData, limitAmount: e?.target?.value })}
                className="w-full pl-32 pr-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* Alert Thresholds */}
          <div className="mb-20">
            <label className="block text-sm font-medium text-foreground mb-8">
              Alert Thresholds
            </label>
            <div className="grid grid-cols-2 gap-12">
              <div>
                <label className="block text-xs text-muted-foreground mb-4">First Alert (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData?.alertThreshold1}
                  onChange={(e) => setFormData({ ...formData, alertThreshold1: parseInt(e?.target?.value) })}
                  className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-4">Second Alert (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData?.alertThreshold2}
                  onChange={(e) => setFormData({ ...formData, alertThreshold2: parseInt(e?.target?.value) })}
                  className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
                />
              </div>
            </div>
          </div>

          {/* Rollover Option */}
          <div className="mb-24">
            <label className="flex items-center gap-12 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.rolloverEnabled}
                onChange={(e) => setFormData({ ...formData, rolloverEnabled: e?.target?.checked })}
                className="w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-foreground">Enable rollover for unused budget</span>
            </label>
          </div>

          {/* Recurring Budget Option */}
          <div className="mb-20">
            <label className="flex items-center gap-12 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e?.target?.checked })}
                className="w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-foreground">Make this a recurring budget</span>
            </label>
          </div>

          {/* Recurring Options - only show if recurring is enabled */}
          {formData?.isRecurring && (
            <>
              {/* Frequency */}
              <div className="mb-20">
                <label className="block text-sm font-medium text-foreground mb-8">
                  Recurrence Frequency
                </label>
                <select
                  value={formData?.recurrenceFrequency}
                  onChange={(e) => setFormData({ ...formData, recurrenceFrequency: e?.target?.value })}
                  className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly (Every 2 weeks)</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly (Every 3 months)</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {/* End Date */}
              <div className="mb-20">
                <label className="block text-sm font-medium text-foreground mb-8">
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  value={formData?.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e?.target?.value })}
                  className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
                  placeholder="Leave blank for no end date"
                />
                <p className="text-xs text-muted-foreground mt-4">
                  Leave blank if this budget should continue indefinitely
                </p>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-12">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-20 py-12 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth font-medium min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-20 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px]"
            >
              Add Budget
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBudgetModal;